import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Portal from '@mui/material/Portal';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { manageDisableScrolling } from '../../../ducks/ui.duck';
import usePass from '../../../util/hooks/usePass';
import MobileBookingModal from './MobileBookingModal';

const closedListingButtonSx = (theme) => ({
  borderLeft: `1px solid ${theme.tokens.matterColorNegative}`,
  width: '100%',
  padding: '15px 24px',
  textAlign: 'center',
});

const MobileBookingPanel = ({
  authorDisplayName,
  autoOpenModal = false,
  bookingLimit,
  category,
  currentUserHasOrdersInProgress,
  customLineItemTranslations,
  distanceToLocation,
  fetchLineItemsError,
  fetchLineItemsInProgress,
  fetchTimeSlotsInProgress,
  fetchTimeSlotsError,
  formattedPrice,
  getTranslation,
  initialDates = null,
  isClosed,
  isOwnListing,
  lineItems,
  currentListing,
  minimumNights,
  onFetchTransactionLineItems,
  onSubmit,
  onFetchTimeSlots,
  priceTitle,
  showAnnualPassError,
  showBookingDatesForm,
  timeSlots,
  title,
  voucher,
  voucherValidationError,
  voucherValidationInProgress,
}) => {
  const location = useLocation();
  const [annualPassIsValid] = usePass();

  const dispatch = useDispatch();
  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch],
  );

  useEffect(() => {
    if (autoOpenModal) {
      slideInBookingModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Cleanup function to re-enable scrolling when navigating away
    return () => {
      onManageDisableScrolling('ListingPage.MobileBookingPanel', false);
    };
  }, [onManageDisableScrolling]);

  const slideInBookingModal = useCallback(() => {
    setOpenBookModalSwipe(true);
    onManageDisableScrolling('ListingPage.MobileBookingPanel', true);
    if (isOwnListing || isClosed) {
      window.scrollTo(0, 0);
    }
    // Don't call navigate() here — React Router v7 re-runs the route loader on
    // every navigation, which resets Redux state and closes the modal.
    // The ?book=true deep link is handled by the mount useEffect instead.
  }, [isOwnListing, isClosed, onManageDisableScrolling]);

  const [openBookModalSwipe, setOpenBookModalSwipe] = useState(false);

  const closeBookModal = useCallback(() => {
    setOpenBookModalSwipe(false);
    onManageDisableScrolling('ListingPage.MobileBookingPanel', false);
  }, [setOpenBookModalSwipe, onManageDisableScrolling]);

  const handleSubmit = useCallback((values) => {
    onManageDisableScrolling('ListingPage.MobileBookingPanel', false);
    onSubmit(values);
  }, [onManageDisableScrolling, onSubmit]);

  // Track the previous location to detect actual back navigation (URL change),
  // not just the absence of ?book on the current URL.
  const prevLocationRef = React.useRef(location);
  useEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;

    // Only close the modal if the URL actually changed (back navigation)
    if (prev.pathname !== location.pathname && openBookModalSwipe) {
      closeBookModal();
    }
  }, [location, openBookModalSwipe, closeBookModal]);

  const sx = useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
    }),
    [],
  );
  const _sx = useMemo(() => ({ borderRadius: '300px' }), []);
  const __sx = useMemo(
    () => ({
      width: '100%',
      '.MuiAlert-icon': {
        display: 'flex',
        alignItems: 'center',
      },
    }),
    [],
  );
  const ___sx = useMemo(
    () => ({
      zIndex: 6,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      minHeight: '100vh',
      paddingTop: 4,
      px: 2,
      bgcolor: 'white',
    }),
    [],
  );
  return (
    <>
      <Stack spacing={1}>
        {showAnnualPassError ? (
          <Alert severity="warning" sx={__sx}>
            {showAnnualPassError}
          </Alert>
        ) : null}
        <Box sx={sx}>
          {!annualPassIsValid ? (
            <Typography component="h4" title={priceTitle} variant="h5">
              {formattedPrice}&nbsp;
              <FormattedMessage id="BookingPanel.perNight" />
            </Typography>
          ) : null}

          {showBookingDatesForm ? (
            <Button
              color="success"
              disabled={!!showAnnualPassError}
              fullWidth={annualPassIsValid}
              onClick={slideInBookingModal}
              sx={_sx}
              variant="contained"
            >
              {category === 'expressBooking' ? (
                <FormattedMessage id="BookingPanel.expressCtaButtonMessage" />
              ) : (
                <FormattedMessage id="BookingPanel.ctaButtonMessage" />
              )}
            </Button>
          ) : isClosed ? (
            <Box component="div" sx={closedListingButtonSx}>
              <FormattedMessage id="BookingPanel.closedListingButtonText" />
            </Box>
          ) : null}
        </Box>
      </Stack>

      <Portal>
        <Slide direction="up" in={openBookModalSwipe} mountOnEnter unmountOnExit>
          <Box sx={___sx}>
            <MobileBookingModal
              authorDisplayName={authorDisplayName}
              bookingLimit={bookingLimit}
              currentListing={currentListing}
              distanceToLocation={distanceToLocation}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
              initialDates={initialDates}
              isOwnListing={isOwnListing}
              lineItems={lineItems}
              minimumNights={minimumNights}
              onClose={closeBookModal}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onSubmit={handleSubmit}
              showAnnualPassError={showAnnualPassError}
              showBookingDatesForm={showBookingDatesForm}
              timeSlots={timeSlots}
              title={title}
              voucher={voucher}
              voucherValidationError={voucherValidationError}
              voucherValidationInProgress={voucherValidationInProgress}
            />
          </Box>
        </Slide>
      </Portal>
    </>
  );
};

export default MobileBookingPanel;
